<script lang="ts">
	import type { VertiNodeLevelDetailProps } from "./VertiNodeLevelDetailProps.js";
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import {
		doesVertiNodeFeatureHaveDetail,
		getVertiNodeFeatureDetailPath,
	} from "../../../../core/schema/paths/getVertiNodeFeatureDetailPath.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import Breadcrumbs from "../../../shared/components/Breadcrumbs.svelte";
	import Card from "../../../shared/components/Card.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./VertiNodeLevelDetail.translations.json";
	import Content from "../../../shared/components/Content.svelte";
	import DynamicPicture from "../../../shared/components/Picture/DynamicPicture.svelte";
	import VertiNodeLevelMobileGallery from "../components/VertiNodeLevel/VertiNodeLevelMobileGallery.svelte";
	import VertiNodeLevelDesktopGallery from "../components/VertiNodeLevel/VertiNodeLevelDesktopGallery.svelte";
	import { setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { vertiNodeLevelsPathMap } from "../../../../core/schema/paths/vertiNodeLevelsPathMap.js";
	import Link from "../../../shared/components/Link.svelte";
	import { contactPathMap } from "../../../../core/schema/paths/contactPathMap.js";

	type $$Props = VertiNodeLevelDetailProps;
	const { vertiNodeLevel, vertiNodeFeatures, pageSlug, breadcrumbs } = $$restProps as $$Props;
	const { TENANT_LOCALE, STORAGE_URL } = getGlobalSettings();
	const translate = getTranslate(translations);

	$: [primaryImage, ...otherImages] = vertiNodeLevel.images;
	setContext(thumbnailImageContextKey, primaryImage ? `${STORAGE_URL}/${primaryImage.path}` : undefined);
</script>

<Layout>
	{#if primaryImage}
		<div class="container">
			<div class="flex items-center overflow-hidden rounded-lg">
				<DynamicPicture
					image={primaryImage}
					alt={vertiNodeLevel.name[TENANT_LOCALE]}
					width={1424}
					height={420}
					loading="eager"
					class="relative h-full w-full items-center justify-center overflow-hidden xl:flex xl:h-[26.25rem]"
					imgClass="rounded-lg h-full w-full object-cover inset-0 max-w-[initial] xl:absolute"
					bucket="storage"
				/>
			</div>
		</div>
	{/if}
	<Breadcrumbs pageSlug={pageSlug[TENANT_LOCALE] ?? ""} {breadcrumbs} />
	<section class="container flex justify-center" class:mt-14={!primaryImage}>
		<div class="lg:w-[50rem]">
			<h1 class="m-0 mb-4 flex flex-col p-0 text-lg font-normal leading-[1.1] md:text-xl lg:mb-10 lg:w-3/4">
				<span class="font-bold">{nbspify(`VertiNode ${vertiNodeLevel.name[TENANT_LOCALE]}`)}</span>
			</h1>
			<Content content={vertiNodeLevel.content[TENANT_LOCALE]} />
			{#if otherImages.length > 0}
				<div class="hidden md:block lg:w-[57.5rem]">
					<VertiNodeLevelDesktopGallery level={vertiNodeLevel} images={otherImages} />
				</div>
			{/if}
		</div>
	</section>
	{#if otherImages.length > 0}
		<div class="container md:hidden">
			<VertiNodeLevelMobileGallery level={vertiNodeLevel} images={otherImages} />
		</div>
	{/if}

	<Link
		class="mx-auto mt-8 text-center"
		asButton
		variant="primary"
		href="/{contactPathMap[TENANT_LOCALE]}#footerContact"
	>
		{translate("interestedIn")}
	</Link>

	{#if vertiNodeFeatures.length > 0}
		<section class="container">
			<h2 class="mb-10 text-center">{translate("features")}</h2>
			<div class="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
				{#each vertiNodeFeatures as vertiNodeFeature}
					{@const showLongDescription =
						!doesVertiNodeFeatureHaveDetail(vertiNodeFeature) && vertiNodeFeature.longDescription !== undefined}
					<Card
						image={vertiNodeFeature.mainMedia}
						title={vertiNodeFeature.name[TENANT_LOCALE]}
						descriptionIsContent={showLongDescription}
						description={showLongDescription
							? (vertiNodeFeature.longDescription?.[TENANT_LOCALE] ?? "")
							: vertiNodeFeature.shortDescription[TENANT_LOCALE]}
						urlSlug={doesVertiNodeFeatureHaveDetail(vertiNodeFeature)
							? getVertiNodeFeatureDetailPath(vertiNodeFeature)[TENANT_LOCALE]
							: undefined}
						squaredImage
						containImage
					/>
				{/each}
			</div>
		</section>
	{/if}
	<Link class="mx-auto mt-8 text-center" asButton variant="primary" href="/{vertiNodeLevelsPathMap[TENANT_LOCALE]}">
		{translate("showAllFeatures")}
	</Link>
</Layout>
